/**
 * Iter 67ei — Vehicle swap dialog.
 *
 * Lets a vendor (or admin) reassign a confirmed/pending booking to a
 * replacement car from the same vendor's fleet — typically because the
 * originally-booked vehicle became unavailable on pickup day
 * (mechanical, late return, accident).
 *
 * Product rules:
 *   • Same vendor's fleet only.
 *   • Price stays the same (vendor absorbs any difference).
 *   • Hard-blocks if the replacement has an overlapping booking.
 *   • Sends an automatic customer email on success.
 *
 * UI:
 *   - Trigger button rendered next to the booking-actions menu and on
 *     the Rental Agreement page (this component owns both the trigger
 *     button + the dialog itself for clean reuse).
 *   - Picker cards: each candidate car shows make/model/year, plate,
 *     daily price, availability pill. Unavailable cars are dimmed +
 *     disabled with a reason tooltip.
 *   - "Reason" textarea required (≥1 char) — gets logged in the audit
 *     trail AND included verbatim in the customer email.
 */
import { useEffect, useState } from "react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Repeat2, Loader2, Car as CarIcon, AlertTriangle, Check, ChevronRight, Gift } from "lucide-react";
import { toast } from "sonner";

// Iter 67ek — apology-credit preset chips. 100 points = €1.
// Cap matches the backend hard-limit (5000 = €50/swap).
const APOLOGY_PRESETS = [
  { label: "None",  points: 0    },
  { label: "€2.50", points: 250  },
  { label: "€5",    points: 500  },
  { label: "€10",   points: 1000 },
];

export default function VehicleSwapDialog({
  booking,
  onSwapped,
  buttonLabel = "Swap vehicle",
  buttonVariant = "outline",
  buttonClassName = "rounded-full",
  buttonSize = "sm",
}) {
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  // Iter 67ek — apology loyalty credit (service-recovery gesture).
  // Tracked as a `points` integer; the UI flips between preset chips
  // and a manual input. Defaults to 0 (off).
  const [apologyPoints, setApologyPoints] = useState(0);
  const [apologyCustom, setApologyCustom] = useState(false);

  // Don't show the trigger for terminal-status bookings. `isSwapEligible`
  // gates rendering further down — but we MUST declare hooks before any
  // conditional return so React's hook order stays stable.
  const status = (booking?.status || "").toLowerCase();
  const isSwapEligible = booking?.id && !["cancelled", "expired", "completed"].includes(status);

  const load = async () => {
    if (!booking?.id) return;
    setLoading(true);
    setPicked(null);
    setReason("");
    setApologyPoints(0);
    setApologyCustom(false);
    try {
      const { data } = await api.get(`/bookings/${booking.id}/swap-candidates`);
      setCandidates(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking?.id]);

  if (!isSwapEligible) return null;

  const submit = async () => {
    if (!picked) { toast.error("Pick a replacement car first"); return; }
    if (!reason.trim()) { toast.error("Tell the customer briefly why the swap is happening"); return; }
    // Iter 67ek — clamp to the same hard cap the backend enforces so a
    // typo doesn't surface as a 422 mid-flow.
    const clampedPoints = Math.max(0, Math.min(5000, Number(apologyPoints) || 0));
    setSaving(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/swap-car`, {
        new_car_id: picked.id,
        reason: reason.trim(),
        apology_points: clampedPoints,
      });
      const credited = data.apology_points || 0;
      toast.success(
        credited > 0
          ? `Booking moved to ${data.swap?.to_car_label || "the replacement car"}. €${(credited / 100).toFixed(2)} credited to the customer.`
          : `Booking moved to ${data.swap?.to_car_label || "the replacement car"}.`
      );
      setOpen(false);
      onSwapped?.(data.booking, data.swap);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant={buttonVariant}
        size={buttonSize}
        className={buttonClassName}
        onClick={() => setOpen(true)}
        data-testid={`swap-vehicle-trigger-${booking.id}`}
      >
        <Repeat2 className="h-3.5 w-3.5 mr-1.5" />
        {buttonLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="swap-vehicle-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium tracking-tight flex items-center gap-2">
              <Repeat2 className="h-5 w-5 text-amber-600" />
              Swap vehicle on booking #{booking.id?.slice(0, 8).toUpperCase()}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Pick a replacement from your fleet. Dates, customer, agreed total
              and deposit all stay the same — only the vehicle changes. The
              customer is automatically notified by email.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-xl bg-neutral-50 border border-border p-3 text-xs flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-1.5 text-neutral-600">
                <CarIcon className="h-3.5 w-3.5 text-neutral-500" />
                <span>Current:</span>
                <span className="font-semibold text-neutral-900">
                  {booking.car?.make || ""} {booking.car?.model || ""} {booking.car?.year ? `· ${booking.car.year}` : ""}
                </span>
              </div>
              <div className="text-neutral-500">
                {booking.start_date?.slice(0, 10)} → {booking.end_date?.slice(0, 10)}
              </div>
            </div>

            {loading ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-neutral-500">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading your fleet…
              </div>
            ) : candidates.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-neutral-500" data-testid="swap-no-candidates">
                You have no other active cars in your fleet to swap to.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[320px] overflow-y-auto pr-1" data-testid="swap-candidate-list">
                {candidates.map((c) => {
                  const isPicked = picked?.id === c.id;
                  const disabled = !c.is_available;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => setPicked(c)}
                      className={`text-left rounded-xl border-2 p-3 transition flex gap-3 ${
                        isPicked
                          ? "border-amber-500 bg-amber-50/60 shadow-sm"
                          : disabled
                          ? "border-border bg-neutral-50/60 opacity-60 cursor-not-allowed"
                          : "border-border hover:border-amber-300 hover:bg-amber-50/30"
                      }`}
                      data-testid={`swap-candidate-${c.id}`}
                      title={disabled ? `Has ${c.conflict_count + c.block_count} conflict(s) in this window` : ""}
                    >
                      <div className="h-14 w-20 rounded-lg overflow-hidden bg-neutral-100 shrink-0 flex items-center justify-center">
                        {c.image ? (
                          <img src={c.image.startsWith("http") ? c.image : fileUrl(c.image)} alt={`${c.make} ${c.model}`} className="w-full h-full object-cover" />
                        ) : (
                          <CarIcon className="h-5 w-5 text-neutral-400" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{c.make} {c.model}</div>
                        <div className="text-[11px] text-neutral-500 truncate">
                          {c.year || ""}{c.year && c.plate ? " · " : ""}{c.plate ? <span className="font-mono">{c.plate}</span> : ""}
                          {c.body_type ? ` · ${c.body_type}` : ""}
                        </div>
                        <div className="flex items-center justify-between mt-1.5">
                          <div className="text-[10px] font-bold uppercase tracking-wider">
                            {c.is_available ? (
                              <span className="text-emerald-700 inline-flex items-center gap-1"><Check className="h-3 w-3" /> Available</span>
                            ) : (
                              <span className="text-rose-700 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Booked</span>
                            )}
                          </div>
                          {c.daily_price != null && (
                            <div className="text-[11px] text-neutral-600">
                              €{Number(c.daily_price).toFixed(0)}<span className="text-neutral-400">/day</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {isPicked && <ChevronRight className="h-4 w-4 text-amber-600 shrink-0 mt-1" />}
                    </button>
                  );
                })}
              </div>
            )}

            <div>
              <Label className="text-xs">Reason (sent to the customer + logged)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="rounded-xl mt-1 text-sm"
                placeholder="e.g. The Tesla had a flat battery yesterday and the service is overrun — switching you to our Audi Q5 at no extra cost."
                data-testid="swap-reason"
              />
            </div>

            {/* Iter 67ek — apology loyalty credit. Optional service-recovery
                gesture. 100 points = €1, capped at 5000 (€50). When set,
                the customer's loyalty balance is bumped + the apology line
                appears in the customer email. */}
            <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-teal-50/40 p-3" data-testid="swap-apology-credit">
              <div className="flex items-start gap-2 mb-2">
                <Gift className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-xs font-semibold text-emerald-900">
                    Apology loyalty credit <span className="font-normal text-emerald-700/70">(optional)</span>
                  </div>
                  <div className="text-[11px] text-emerald-800/80 leading-snug mt-0.5">
                    Soften the bad news — credit the customer some loyalty points they can redeem
                    on their next booking. 100 points = €1.
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {APOLOGY_PRESETS.map((p) => {
                  const isActive = !apologyCustom && apologyPoints === p.points;
                  return (
                    <button
                      key={p.points}
                      type="button"
                      onClick={() => { setApologyPoints(p.points); setApologyCustom(false); }}
                      className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition ${
                        isActive
                          ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                          : "bg-white text-emerald-800 border-emerald-200 hover:border-emerald-400"
                      }`}
                      data-testid={`swap-apology-preset-${p.points}`}
                    >
                      {p.label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => { setApologyCustom(true); }}
                  className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition ${
                    apologyCustom
                      ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                      : "bg-white text-emerald-800 border-emerald-200 hover:border-emerald-400"
                  }`}
                  data-testid="swap-apology-custom-toggle"
                >
                  Custom
                </button>
                {apologyCustom && (
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      min={0}
                      max={5000}
                      step={50}
                      value={apologyPoints}
                      onChange={(e) => setApologyPoints(Math.max(0, Math.min(5000, Number(e.target.value) || 0)))}
                      className="w-24 h-7 rounded-full px-2 text-[11px] font-mono border border-emerald-300 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      data-testid="swap-apology-input"
                      aria-label="Apology points"
                    />
                    <span className="text-[11px] text-emerald-800">pts</span>
                  </div>
                )}
              </div>
              {apologyPoints > 0 && (
                <div className="mt-2 text-[11px] text-emerald-900 font-medium" data-testid="swap-apology-summary">
                  → Customer gets <strong>{apologyPoints} points (€{(apologyPoints / 100).toFixed(2)})</strong> credited immediately.
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex flex-row justify-end gap-2 mt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid="swap-cancel">Cancel</Button>
            <Button
              type="button"
              onClick={submit}
              disabled={saving || !picked || !reason.trim()}
              className="rounded-full bg-amber-600 hover:bg-amber-700"
              data-testid="swap-confirm"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Repeat2 className="h-3.5 w-3.5 mr-1.5" />}
              Confirm swap
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
